// The two draw games, as pure logic: the weekly raffle
// (gno.land/r/splash/arcade/raffle) and the hourly one
// (gno.land/r/splash/arcade/speed).
//
// Same discipline as board.js. Every number here comes from a realm's EXPORTED
// READ — PotSize, TicketTotal(OfWeek|OfWindow), CurrentWeek/CurrentWindow,
// DrawOf, RefundOpensAt, the Weeks/Windows listers — and nothing parses
// Render(). One composition, used by the live client and the rehearsal alike,
// so the two cannot drift. The interface a client must give the functions here:
//
//   currentIndex() -> bigint          potSize() -> ugnot
//   ticketTotal() -> units            ticketTotalOf(i) -> units
//   ticketsOf(addr) -> units          ticketsOfIndex(i, addr) -> units
//   spendOfIndex(i, addr) -> ugnot    drawOf(i) -> record
//   refundOpensAt(i) -> height        capturedRandomness(i) -> hex | ''
//   count() -> int                    indexes(offset, limit) -> bigint[]
//   escrowedCut(i) -> ugnot
//   (raffle only) sponsoredOf(i), claimableOf(i, addr), earnedOfIndex(i, addr),
//                 claimPerUnitOf(i)
//
// "Units" is the realms' word and it is the number a player thinks of as
// ENTRIES: the raffle sells a 10 GNOT ticket worth 40 of them (TicketPrice /
// EntryUnitPrice), the hourly realm sells a 1 GNOT ticket worth exactly one
// and says in Buy not to give it an entry unit.
import { CONFIG } from './config.js';

// --- paging ----------------------------------------------------------------
// Weeks/Windows panic on a negative offset or limit and clamp a limit above
// PageLimit. Both listers, live and rehearsal, pass their arguments through
// here so a bad window is refused before it reaches the chain and a big one is
// trimmed to what the realm would answer anyway.
export function drawWindow(offset, limit) {
  const o = Number(offset), l = Number(limit);
  if (!Number.isInteger(o) || o < 0) throw new Error(`draw: negative offset ${offset}`);
  if (!Number.isInteger(l) || l < 0) throw new Error(`draw: negative limit ${limit}`);
  return { offset: o, limit: Math.min(l, CONFIG.pageLimit) };
}

// The offset of the last `n` recorded draws. The listers are ASCENDING and
// have no reverse, so the newest page is the tail: WeekCount() - n, floored at
// zero. Returns null when there is nothing recorded at all.
export function tailWindow(count, n) {
  const c = Number(count);
  if (!Number.isInteger(c) || c < 0) throw new Error(`draw: bad count ${count}`);
  if (c === 0) return null;
  const limit = Math.min(n, c, CONFIG.pageLimit);
  return { offset: c - limit, limit };
}

// --- the DrawOf tuples -----------------------------------------------------
//
// raffle.DrawOf: committed, frozenTotal, round, pot, resolved, winner,
//                abandoned, sponsored, committedAt      (nine values)
// speed.DrawOf:  committed, frozenTotal, round, pot, resolved, winner,
//                abandoned, committedAt                 (eight — THERE IS NO
//                SPONSORED ELEMENT, because that realm's pot is entry money
//                only and it says so at DrawOf)
//
// Normalised to one record with sponsored = 0n for the hourly game, so the
// views and the state machine below are written once. The arity is CHECKED, not
// assumed: if a realm's tuple ever grows or shrinks this throws instead of
// silently reading `pot` out of the `round` slot.
export function normaliseDraw(values, kind) {
  const want = kind === 'raffle' ? 9 : 8;
  if (!Array.isArray(values) || values.length !== want) {
    throw new Error(`draw: ${kind} DrawOf returned ${values?.length} values, expected ${want}`);
  }
  const [committed, frozenTotal, round, pot, resolved, winner, abandoned] = values;
  const sponsored = kind === 'raffle' ? values[7] : 0n;
  const committedAt = kind === 'raffle' ? values[8] : values[7];
  return {
    committed: !!committed,
    frozenTotal: BigInt(frozenTotal),
    round: BigInt(round),
    pot: BigInt(pot),
    resolved: !!resolved,
    winner: winner || '',
    abandoned: !!abandoned,
    sponsored: BigInt(sponsored),
    committedAt: BigInt(committedAt),
  };
}

// A record for an index the realm has never heard of. DrawOf answers exactly
// this (all zero) for such a week, so the shape is the realm's, not ours.
export const emptyDraw = () => ({
  committed: false, frozenTotal: 0n, round: 0n, pot: 0n,
  resolved: false, winner: '', abandoned: false, sponsored: 0n, committedAt: 0n,
});

// --- the state machine -----------------------------------------------------
//
// Every branch is a state one of the two realms actually distinguishes, and
// the two the realms are most emphatic about are separate here for the reason
// they give at DrawOf: a draw WAITING ON ITS RANDOMNESS and a draw ABANDONED
// are not both "unresolved", and showing them the same way lies to a buyer
// about whether to expect a winner or go and take their money back.
//
//   selling            this is the live week/window; tickets are open
//   needs-commit       it is over, sold entries, nobody has committed it yet
//   drawless           it is over and sold nothing; there is no rollover
//   waiting-beacon     committed; its pinned drand round is not published yet
//   ready              committed; the round is out and anyone may Resolve
//   resolved           a winner was named and paid
//   abandoned          it will never draw; refunds are open
export function drawState(rec, { index, current, beaconRound, liveTickets }) {
  const i = BigInt(index), cur = BigInt(current);
  if (i >= cur) return 'selling';
  if (rec.abandoned) return 'abandoned';
  if (rec.resolved) return 'resolved';
  if (!rec.committed) return BigInt(liveTickets ?? 0n) > 0n ? 'needs-commit' : 'drawless';
  if (beaconRound == null) return 'waiting-beacon';
  return BigInt(beaconRound) >= rec.round ? 'ready' : 'waiting-beacon';
}

// Refund pays a buyer once the grace window is up, and pays immediately on a
// draw already abandoned. RefundOpensAt returns 0 for four unrelated states
// (no record, uncommitted, resolved, abandoned) and its own comment says a
// caller must not read any of them as "refundable now" — so zero is never
// treated as a height here.
export function refundOpen(rec, { height, refundOpensAt }) {
  if (rec.resolved) return false;
  if (rec.abandoned) return true;
  const at = BigInt(refundOpensAt ?? 0n);
  return at > 0n && BigInt(height) >= at;
}

// --- the clock -------------------------------------------------------------
//
// THE REALMS NOW EXPORT THE BOUNDARY, so this page does not compute it and does
// not guess it. Four reads landed in the contracts (raffle.gno
// WeekEndHeight/CurrentWeekEndHeight, speed.gno
// WindowEndHeight/CurrentWindowEndHeight) and the page asks for the one it
// wants in ONE call — see chain/draws.js currentEndHeight, and the reason on
// CurrentWeekEndHeight itself: composing WeekEndHeight(CurrentWeek()) off chain
// is two queries at two chain heights, and a week that turns over between them
// hands back the boundary of a week that has already ended, i.e. a countdown
// that jumps backwards by a whole week exactly when it matters.
//
// There is no longer a configured launch height, no learning-by-watching and no
// "not available": a boundary is either the realm's exact answer or a read that
// FAILED and is shown as one.
//
// THE FENCEPOST. The returned height is the FIRST height of the NEXT period —
// a period is live for [end(i-1), end(i)) — so at exactly that height the realm
// already reports the next index. blocksUntil is therefore 0 AT the boundary,
// never 1: showing "1 block left" there would be the off-by-one both doc
// comments warn about.
export const blocksUntil = (target, height) => {
  const d = BigInt(target) - BigInt(height);
  return d > 0n ? d : 0n;
};

// BLOCKS -> SECONDS IS THE PAGE'S JOB, at a block time PER CHAIN. The realms
// refuse to return seconds (WeekEndHeight: "this realm will not return seconds
// or 'time remaining', because that would bake one clock's rounding into every
// front end"), so the conversion rate is chosen here and its provenance is
// carried with it so the page can say which it used:
//
//   measured    CONFIG.draw.blockSeconds has an observation for this chain id
//   assumed     it does not, so the contracts' own 86400/26182 = 3.29998 s is
//               used AND LABELLED — it is not any measured chain's rate
//   rehearsal   supplied by the rehearsal, whose blocks really do arrive every
//               cadenceMs/2 (drawmock.blockSeconds)
export function blockSecondsFor(chainId, draw = CONFIG.draw) {
  const measured = draw.blockSeconds[chainId];
  if (measured != null) return { seconds: measured, source: 'measured', chainId };
  return { seconds: draw.assumedBlockSeconds, source: 'assumed', chainId };
}

// `blockSeconds` is REQUIRED, deliberately. A default would silently be one
// chain's number on every other chain — and the contracts' 3.29998 s is 3.87
// hours out over a raffle week against onyx-1's measured 3.376 s. The caller
// says which clock it is using or gets an error.
export const secondsFor = (blocks, blockSeconds) => {
  const s = Number(blockSeconds);
  if (!Number.isFinite(s) || s <= 0) {
    throw new Error(`draw: secondsFor needs a positive block time, got ${blockSeconds}`);
  }
  return Math.round(Number(blocks) * s);
};

// --- money -----------------------------------------------------------------

// One ticket's entries: raffle.gno's unitsPerTicket = TicketPrice /
// EntryUnitPrice, which the realm asserts is exact at build time. The hourly
// realm has no entry unit — one ticket is one unit — so it passes null and
// gets 1n.
export function entriesPerTicket(ticketPrice, entryUnitPrice) {
  if (entryUnitPrice == null) return 1n;
  const t = BigInt(ticketPrice), e = BigInt(entryUnitPrice);
  if (e <= 0n) throw new Error('draw: entry unit price must be positive');
  if (t % e !== 0n) throw new Error(`draw: ${t} is not a whole number of ${e}-ugnot entries`);
  return t / e;
}

// What a purchase costs and what it buys. Both realms charge tickets *
// TicketPrice and nothing else: no dynamic pricing, no discount tier, no
// bonding curve (TicketPrice's own comment).
export const ticketCost = (tickets, price) => BigInt(tickets) * BigInt(price);

// The house's 5% is escrowed per draw and earned ONLY by a draw that runs
// (finding 25): it is not in the pot, and a week that never draws refunds it.
// Shown so a buyer can see what part of their payment is prize money.
export const houseCutOf = (cost) => (BigInt(cost) * CONFIG.draw.houseCutBps) / 10_000n;
export const prizeShareOf = (cost) => BigInt(cost) - houseCutOf(cost);

// --- compositions ----------------------------------------------------------

// One row: a draw's record plus the two things DrawOf cannot say — its LIVE
// entry total (DrawOf reports FrozenTotal, which is zero on anything
// uncommitted, so it cannot tell "sold nothing" from "never committed") and
// the height its refund opens at.
export async function readDrawRow(client, index, kind) {
  const i = BigInt(index);
  const [rec, liveTickets, refundOpensAt] = await Promise.all([
    client.drawOf(i),
    client.ticketTotalOf(i),
    client.refundOpensAt(i),
  ]);
  const row = { index: i, ...rec, liveTickets: BigInt(liveTickets), refundOpensAt: BigInt(refundOpensAt) };
  if (kind === 'raffle') {
    const [claimPerUnit] = await Promise.all([client.claimPerUnitOf(i)]);
    row.claimPerUnit = BigInt(claimPerUnit);
  }
  return row;
}

// The last `n` recorded draws, newest first, read through the lister. Mirrors
// readBoard: counts for size and emptiness, ONE page of indexes, then the
// per-record reads. An N+1 on purpose, capped at a page.
export async function readRecent(client, n, kind) {
  const count = Number(await client.count());
  const w = tailWindow(count, n);
  if (!w) return { count, rows: [] };
  const indexes = await client.indexes(w.offset, w.limit);
  const rows = [];
  for (const i of indexes) rows.push(await readDrawRow(client, i, kind));
  rows.reverse();
  return { count, rows };
}

// What the connected player still has in each past draw. Only asked for the
// draws where an answer could change what they see: a resolved draw owes nobody
// anything, so it is skipped rather than read and discarded.
//
// ClaimableOf reads earnedOf and NOT ticketsOf, which the realm had to fix
// (finding 34) so the read and ClaimDeadWeek agree; EarnedOfWeek is read
// alongside it because "you hold 412 entries and your claim is on 12 of them"
// is the only explanation of a zero claim a buyer will accept.
export async function readMine(client, rows, addr, kind) {
  const out = new Map();
  if (!addr) return out;
  for (const row of rows) {
    if (row.resolved) continue;
    const mine = { spend: 0n, entries: 0n, claimable: 0n, earned: 0n };
    mine.spend = BigInt(await client.spendOfIndex(row.index, addr));
    mine.entries = BigInt(await client.ticketsOfIndex(row.index, addr));
    if (kind === 'raffle' && row.claimPerUnit > 0n) {
      mine.claimable = BigInt(await client.claimableOf(row.index, addr));
      mine.earned = BigInt(await client.earnedOfIndex(row.index, addr));
    }
    out.set(String(row.index), mine);
  }
  return out;
}

// The live week/window: the hero numbers, and the player's own stake in it.
export async function readLive(client, addr, kind) {
  const index = BigInt(await client.currentIndex());
  const [pot, entries, rec, mine] = await Promise.all([
    client.potSize(),
    client.ticketTotal(),
    client.drawOf(index),
    addr ? client.ticketsOf(addr) : 0n,
  ]);
  const live = {
    index,
    pot: BigInt(pot),
    entries: BigInt(entries),
    mine: BigInt(mine),
    sponsored: kind === 'raffle' ? BigInt(rec.sponsored) : 0n,
    round: rec.round,
    committed: rec.committed,
  };
  // What the buyers put in versus what the arcade added. DrawOf's own comment:
  // "pot minus sponsored is what the week's buyers put in and could reclaim,
  // and sponsored is what the arcade added on top."
  live.fromBuyers = live.pot - live.sponsored;
  return live;
}
